import React, { useState, useEffect } from 'react';
import { statisticsApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, FolderKanban, Users, MapPin, Calendar } from 'lucide-react';

export default function StatisticsPage() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchStatistics();
    }, []);

    const fetchStatistics = async () => {
        try {
            const response = await statisticsApi.get();
            setStats(response.data);
        } catch (error) {
            toast.error('Errore nel caricamento delle statistiche');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="text-center py-16 text-slate-500">
                Nessuna statistica disponibile
            </div>
        );
    }

    const maxClientCount = Math.max(...stats.projects_by_client.map(c => c.count), 1);
    const maxCityCount = Math.max(...stats.projects_by_city.map(c => c.count), 1);
    const maxTypeCount = Math.max(...stats.projects_by_type.map(c => c.count), 1);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Statistiche
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                    Panoramica dei tuoi progetti
                </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-white border-slate-200 rounded-sm hover:border-slate-300 transition-colors">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <CardDescription className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Totale Progetti
                            </CardDescription>
                            <FolderKanban className="w-4 h-4 text-slate-400" />
                        </div>
                    </CardHeader>
                    <CardContent>
                        <p className="text-3xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid="total-projects">
                            {stats.total_projects}
                        </p>
                    </CardContent>
                </Card>

                <Card className="bg-white border-slate-200 rounded-sm hover:border-slate-300 transition-colors">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <CardDescription className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Clienti Unici
                            </CardDescription>
                            <Users className="w-4 h-4 text-slate-400" />
                        </div>
                    </CardHeader>
                    <CardContent>
                        <p className="text-3xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid="unique-clients">
                            {stats.projects_by_client.length}
                        </p>
                    </CardContent>
                </Card>

                <Card className="bg-white border-slate-200 rounded-sm hover:border-slate-300 transition-colors">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <CardDescription className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Città Coperte
                            </CardDescription>
                            <MapPin className="w-4 h-4 text-slate-400" />
                        </div>
                    </CardHeader>
                    <CardContent>
                        <p className="text-3xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid="unique-cities">
                            {stats.projects_by_city.length}
                        </p>
                    </CardContent>
                </Card>

                <Card className="bg-white border-slate-200 rounded-sm hover:border-slate-300 transition-colors">
                    <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                            <CardDescription className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Mesi Attivi
                            </CardDescription>
                            <Calendar className="w-4 h-4 text-slate-400" />
                        </div>
                    </CardHeader>
                    <CardContent>
                        <p className="text-3xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid="active-months">
                            {stats.projects_by_month.length}
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Charts using CSS bars */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Projects by Client */}
                <Card className="bg-white border-slate-200 rounded-sm">
                    <CardHeader>
                        <CardTitle className="text-lg" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            Progetti per Cliente
                        </CardTitle>
                        <CardDescription>Top clienti per numero di progetti</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {stats.projects_by_client.length > 0 ? (
                            <div className="space-y-3">
                                {stats.projects_by_client.slice(0, 8).map((item, index) => (
                                    <div key={index} className="space-y-1">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-700 truncate max-w-[200px]">{item.name}</span>
                                            <span className="text-slate-500 font-mono">{item.count}</span>
                                        </div>
                                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                            <div 
                                                className="h-full bg-blue-700 rounded-full transition-all duration-500"
                                                style={{ width: `${(item.count / maxClientCount) * 100}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="py-8 text-center text-slate-500">
                                Nessun dato disponibile
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Projects by City */}
                <Card className="bg-white border-slate-200 rounded-sm">
                    <CardHeader>
                        <CardTitle className="text-lg" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            Progetti per Città
                        </CardTitle>
                        <CardDescription>Distribuzione geografica dei progetti</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {stats.projects_by_city.length > 0 ? (
                            <div className="space-y-3">
                                {stats.projects_by_city.slice(0, 8).map((item, index) => (
                                    <div key={index} className="space-y-1">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-700 truncate max-w-[200px]">{item.name}</span>
                                            <span className="text-slate-500 font-mono">{item.count}</span>
                                        </div>
                                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                            <div 
                                                className="h-full bg-emerald-600 rounded-full transition-all duration-500"
                                                style={{ width: `${(item.count / maxCityCount) * 100}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="py-8 text-center text-slate-500">
                                Nessun dato disponibile
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Projects by Type */}
                <Card className="bg-white border-slate-200 rounded-sm">
                    <CardHeader>
                        <CardTitle className="text-lg" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            Progetti per Tipologia
                        </CardTitle>
                        <CardDescription>Distribuzione per tipo di progetto</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {stats.projects_by_type.length > 0 ? (
                            <div className="space-y-3">
                                {stats.projects_by_type.slice(0, 8).map((item, index) => (
                                    <div key={index} className="space-y-1">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-700 truncate max-w-[200px]">{item.name}</span>
                                            <span className="text-slate-500 font-mono">{item.count}</span>
                                        </div>
                                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                            <div 
                                                className="h-full bg-amber-500 rounded-full transition-all duration-500"
                                                style={{ width: `${(item.count / maxTypeCount) * 100}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="py-8 text-center text-slate-500">
                                Nessun dato disponibile
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Projects by Month */}
                <Card className="bg-white border-slate-200 rounded-sm">
                    <CardHeader>
                        <CardTitle className="text-lg" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            Andamento Mensile
                        </CardTitle>
                        <CardDescription>Progetti registrati negli ultimi mesi</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {stats.projects_by_month.length > 0 ? (
                            <div className="flex items-end gap-2 h-[200px]">
                                {[...stats.projects_by_month].reverse().slice(0, 12).map((item, index) => {
                                    const maxMonthCount = Math.max(...stats.projects_by_month.map(m => m.count), 1);
                                    const heightPercent = (item.count / maxMonthCount) * 100;
                                    return (
                                        <div key={index} className="flex-1 flex flex-col items-center gap-1">
                                            <span className="text-xs text-slate-500 font-mono">{item.count}</span>
                                            <div 
                                                className="w-full bg-blue-700 rounded-t transition-all duration-500"
                                                style={{ height: `${Math.max(heightPercent, 5)}%` }}
                                            />
                                            <span className="text-xs text-slate-400 transform -rotate-45 origin-left whitespace-nowrap">
                                                {item.name}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="py-8 text-center text-slate-500">
                                Nessun dato disponibile
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
